import type { FC } from "react";

import { AddressPicker } from "@/shared/components/ui/AddressPicker";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { EMPTY_ADDRESS } from "@/shared/utils/addressUtils";
import type { AddressParts } from "@/shared/utils/addressUtils";
import type { EstateItem } from "@/shared/types/estateTypes";

export const LOCATION_TYPES = ["Bank Safe Deposit Box", "Home Safe", "Storage Facility", "Other"];

export interface SafeDepositFormValues {
  id: string;
  locationType: string;
  location: string;
  address: AddressParts;
  boxNumber: string;
}

export const EMPTY_SAFE_DEPOSIT_FORM: SafeDepositFormValues = {
  id: "",
  locationType: "",
  location: "",
  address: { ...EMPTY_ADDRESS },
  boxNumber: "",
};

export interface SafeDepositFieldErrors {
  locationType?: string;
  location?: string;
}

export function validateSafeDepositFields(form: SafeDepositFormValues): SafeDepositFieldErrors {
  if (!form.locationType) return { locationType: "Please select a location type" };
  if (!form.location.trim()) return { location: "Please enter a location" };
  return {};
}

export function safeDepositToItemFields(form: SafeDepositFormValues) {
  return {
    category: "Safe or safety deposit box" as const,
    classification: "asset" as const,
    item_name: form.location.trim(),
    item_type: form.locationType || undefined,
    metadata: {
      address: form.address,
      box_number: form.boxNumber.trim() || null,
    },
  };
}

export function parseSafeDepositToForm(item: EstateItem): SafeDepositFormValues {
  const m = (item.metadata ?? {}) as Record<string, unknown>;
  return {
    id: String(item.id),
    locationType: item.item_type ?? "",
    location: item.item_name ?? "",
    address: (m.address as AddressParts) ?? { ...EMPTY_ADDRESS },
    boxNumber: (m.box_number as string) ?? "",
  };
}

interface SafeDepositFormProps {
  form: SafeDepositFormValues;
  onChange: (form: SafeDepositFormValues) => void;
  fieldErrors?: SafeDepositFieldErrors;
}

export const SafeDepositForm: FC<SafeDepositFormProps> = ({ form, onChange, fieldErrors }) => {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1.5">
        <Label>Location type</Label>
        <Select
          value={form.locationType}
          onValueChange={(val) => onChange({ ...form, locationType: val })}
        >
          <SelectTrigger className={fieldErrors?.locationType ? "border-destructive" : ""}>
            <SelectValue placeholder="Select location type" />
          </SelectTrigger>
          <SelectContent>
            {LOCATION_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldError message={fieldErrors?.locationType} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Location</Label>
        <Input
          value={form.location}
          onChange={(e) => onChange({ ...form, location: e.target.value })}
          placeholder="e.g., Chase Bank"
          className={fieldErrors?.location ? "border-destructive" : ""}
        />
        <FieldError message={fieldErrors?.location} />
      </div>

      <div className="flex flex-col gap-1.5">
        <AddressPicker
          value={form.address}
          onChange={(address) => onChange({ ...form, address })}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>
          Box number <span className="font-normal text-secondary">(if applicable)</span>
        </Label>
        <Input
          value={form.boxNumber}
          onChange={(e) => onChange({ ...form, boxNumber: e.target.value })}
          placeholder="Box or unit number"
        />
      </div>
    </div>
  );
};
